import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Upload, Plus, Search, Edit, Trash2, Eye, CheckCircle, XCircle, Loader, FileSpreadsheet, Filter, X, ChevronDown, Save, FileText, Sparkles } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase, getAuthHeaders } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';

// Configurar worker do PDF.js com URL do npm
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

interface Asset {
  id: string;
  asset_code: string;
  dynamic_data: Record<string, any>;
  status: string;
  created_at: string;
}

interface DynamicField {
  id: string;
  field_name: string;
  field_type: string;
  field_order: number;
}

interface UploadStep {
  id: number;
  title: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  message: string;
}

interface FilterCondition {
  id: string;
  field: string;
  operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'not_contains' | 'not_equals';
  value: string;
}

interface ExcelColumn {
  name: string;
  selected: boolean;
  sampleData: string[];
}

export default function Assets() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [fields, setFields] = useState<DynamicField[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadSteps, setUploadSteps] = useState<UploadStep[]>([
    { id: 1, title: 'Validar arquivo Excel', status: 'pending', message: 'Aguardando...' },
    { id: 2, title: 'Criar campos dinâmicos', status: 'pending', message: 'Aguardando...' },
    { id: 3, title: 'Importar ativos', status: 'pending', message: 'Aguardando...' },
    { id: 4, title: 'Finalizar importação', status: 'pending', message: 'Aguardando...' }
  ]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Estados para filtros avançados
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Estados para o formulário de novo ativo
  const [newAssetData, setNewAssetData] = useState<Record<string, string>>({});
  const [isSavingAsset, setIsSavingAsset] = useState(false);

  // Estados para edição de ativo
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedAssetData, setEditedAssetData] = useState<Record<string, string>>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Estados para importação de PDF
  const [showPdfImportModal, setShowPdfImportModal] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [pdfUploadSteps, setPdfUploadSteps] = useState<UploadStep[]>([
    { id: 1, title: 'Ler arquivo PDF', status: 'pending', message: 'Aguardando...' },
    { id: 2, title: 'Extrair e analisar texto', status: 'pending', message: 'Aguardando...' },
    { id: 3, title: 'Mapear campos inteligentemente', status: 'pending', message: 'Aguardando...' },
    { id: 4, title: 'Criar campos e importar dados', status: 'pending', message: 'Aguardando...' }
  ]);

  // Estados para seleção de colunas
  const [showColumnSelection, setShowColumnSelection] = useState(false);
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [isProcessingImport, setIsProcessingImport] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        console.log('🔒 Usuário não autenticado, redirecionando para login...');
        navigate('/login');
        return;
      }

      console.log('✅ Usuário autenticado:', session.user.email);
      loadAssets();
      loadFields();
    } catch (error) {
      console.error('❌ Erro ao verificar autenticação:', error);
      navigate('/login');
    }
  };

  const loadAssets = async () => {
    try {
      const { data, error } = await supabase
        .from('assets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssets(data || []);
    } catch (error) {
      console.error('Erro ao carregar ativos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      const { data, error } = await supabase
        .from('dynamic_fields')
        .select('*')
        .order('field_order', { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error) {
      console.error('Erro ao carregar campos:', error);
    }
  };

  const updateStep = (stepId: number, status: UploadStep['status'], message: string) => {
    setUploadSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, message } : step
    ));
  };

  const resetSteps = () => {
    setUploadSteps([
      { id: 1, title: 'Validar arquivo Excel', status: 'pending', message: 'Aguardando...' },
      { id: 2, title: 'Criar campos dinâmicos', status: 'pending', message: 'Aguardando...' },
      { id: 3, title: 'Importar ativos', status: 'pending', message: 'Aguardando...' },
      { id: 4, title: 'Finalizar importação', status: 'pending', message: 'Aguardando...' }
    ]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      resetSteps();
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || isUploading) return;

    setIsUploading(true);

    try {
      // PASSO 1: Validar arquivo e ler dados
      updateStep(1, 'processing', 'Lendo arquivo Excel...');

      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });

      console.log('📊 Dados lidos do Excel:', jsonData);

      if (!jsonData || jsonData.length === 0) {
        updateStep(1, 'error', 'Arquivo vazio ou inválido');
        setIsUploading(false);
        return;
      }

      if (jsonData.length < 2) {
        updateStep(1, 'error', 'Arquivo precisa ter cabeçalho e pelo menos uma linha de dados');
        setIsUploading(false);
        return;
      }

      const headers = (jsonData[0] as string[]).filter(h => h && h.trim() !== '');
      const rows = jsonData.slice(1);

      if (!headers || headers.length === 0) {
        updateStep(1, 'error', 'Cabeçalho não encontrado');
        setIsUploading(false);
        return;
      }

      // Filtrar linhas vazias
      const validRows = rows.filter((row: any) => {
        if (!Array.isArray(row)) return false;
        return row.some(cell => cell !== null && cell !== undefined && cell !== '');
      });

      if (validRows.length === 0) {
        updateStep(1, 'error', 'Nenhuma linha válida encontrada');
        setIsUploading(false);
        return;
      }

      updateStep(1, 'success', `${validRows.length} linhas válidas encontradas`);
      console.log('✅ Validação concluída:', { headers, totalRows: validRows.length });

      // Preparar dados das colunas com amostras
      const columnsWithSamples: ExcelColumn[] = headers.map((header, index) => {
        const samples = validRows
          .slice(0, 3) // Pegar até 3 amostras
          .map((row: any) => {
            const value = row[index];
            return value !== undefined && value !== null && value !== '' 
              ? String(value).substring(0, 50) // Limitar tamanho da amostra
              : '-';
          });

        return {
          name: header.trim(),
          selected: true, // Todas selecionadas por padrão
          sampleData: samples
        };
      });

      // Salvar dados para uso posterior
      setExcelData([headers, ...validRows]);
      setExcelColumns(columnsWithSamples);

      // Fechar modal de upload e abrir modal de seleção de colunas
      setShowImportModal(false);
      setShowColumnSelection(true);
      setIsUploading(false);

    } catch (error) {
      console.error('💥 Erro crítico no upload:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      const currentStep = uploadSteps.find(s => s.status === 'processing');
      if (currentStep) {
        updateStep(currentStep.id, 'error', errorMessage);
      } else {
        updateStep(1, 'error', errorMessage);
      }
      
      setIsUploading(false);
    }
  };

  const toggleColumnSelection = (columnName: string) => {
    setExcelColumns(prev => prev.map(col => 
      col.name === columnName ? { ...col, selected: !col.selected } : col
    ));
  };

  const selectAllColumns = () => {
    setExcelColumns(prev => prev.map(col => ({ ...col, selected: true })));
  };

  const deselectAllColumns = () => {
    setExcelColumns(prev => prev.map(col => ({ ...col, selected: false })));
  };

  const handleConfirmImport = async () => {
    if (isProcessingImport) return;

    const selectedColumns = excelColumns.filter(col => col.selected);

    if (selectedColumns.length === 0) {
      alert('Selecione pelo menos uma coluna para importar');
      return;
    }

    setIsProcessingImport(true);

    try {
      console.log('🚀 Iniciando importação com colunas selecionadas:', selectedColumns.map(c => c.name));

      // Reabrir modal de progresso
      setShowColumnSelection(false);
      setShowImportModal(true);
      resetSteps();

      // PASSO 2: Criar campos dinâmicos
      updateStep(2, 'processing', 'Verificando e criando campos no banco de dados...');

      const headers = excelData[0] as string[];
      const rows = excelData.slice(1);

      // Filtrar apenas colunas selecionadas
      const selectedColumnNames = selectedColumns.map(c => c.name);
      const selectedIndices = headers
        .map((header, index) => selectedColumnNames.includes(header) ? index : -1)
        .filter(index => index !== -1);

      console.log('📋 Índices das colunas selecionadas:', selectedIndices);

      // Verificar sessão
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('❌ Erro de sessão:', sessionError);
        updateStep(2, 'error', 'Sessão expirada. Faça login novamente.');
        setIsProcessingImport(false);
        navigate('/login');
        return;
      }

      console.log('✅ Sessão válida, usuário:', session.user.id);

      // Buscar campos existentes
      console.log('🔍 Buscando campos existentes...');
      const { data: existingFieldsBefore, error: fetchError } = await supabase
        .from('dynamic_fields')
        .select('field_name, id')
        .order('field_order', { ascending: true });

      if (fetchError) {
        console.error('❌ Erro ao buscar campos:', fetchError);
        updateStep(2, 'error', `Erro ao buscar campos: ${fetchError.message}`);
        setIsProcessingImport(false);
        return;
      }

      console.log('📋 Campos existentes antes da importação:', existingFieldsBefore?.length || 0);

      const existingFieldNames = new Set(existingFieldsBefore?.map(f => f.field_name) || []);

      // Identificar campos novos (apenas das colunas selecionadas)
      const newFieldNames = selectedColumnNames.filter(name => !existingFieldNames.has(name));
      
      console.log('🆕 Campos novos detectados:', newFieldNames.length);
      console.log('📝 Novos campos:', newFieldNames);

      // Criar apenas campos novos
      const newFields = newFieldNames.map((name, index) => ({
        field_name: name,
        field_type: 'text',
        field_order: (existingFieldsBefore?.length || 0) + index,
        is_required: false,
        created_by: session.user.id
      }));

      let fieldsCreatedCount = 0;

      if (newFields.length > 0) {
        console.log('💾 Inserindo campos no banco...');
        
        for (const field of newFields) {
          const { error: insertError } = await supabase
            .from('dynamic_fields')
            .insert([field]);

          if (insertError) {
            console.error(`❌ Erro ao criar campo "${field.field_name}":`, insertError);
            if (!insertError.message.includes('duplicate') && !insertError.message.includes('unique')) {
              updateStep(2, 'error', `Erro ao criar campo "${field.field_name}": ${insertError.message}`);
              setIsProcessingImport(false);
              return;
            }
          } else {
            fieldsCreatedCount++;
            console.log(`✅ Campo "${field.field_name}" criado com sucesso`);
          }
        }

        console.log(`✅ Total de campos criados: ${fieldsCreatedCount}`);
      } else {
        console.log('ℹ️ Todos os campos selecionados já existem');
      }

      updateStep(2, 'success', `${fieldsCreatedCount} novos campos criados (${existingFieldNames.size} já existiam)`);

      await new Promise(resolve => setTimeout(resolve, 500));

      // PASSO 3: Importar ativos
      updateStep(3, 'processing', 'Importando ativos...');

      console.log('📦 Preparando dados dos ativos...');

      // Preparar dados dos ativos (apenas com colunas selecionadas)
      const assetsToImport = rows
        .map((row: any[], rowIndex: number) => {
          const dynamicData: any = {};
          
          selectedIndices.forEach(index => {
            const header = headers[index];
            const value = row[index];
            dynamicData[header] = value !== undefined && value !== null ? String(value).trim() : '';
          });

          // Verificar se tem pelo menos um campo preenchido
          const hasData = Object.values(dynamicData).some(v => v !== '');
          
          if (!hasData) {
            console.log(`⚠️ Linha ${rowIndex + 2} vazia, ignorando...`);
            return null;
          }

          return {
            asset_code: `ASSET-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            dynamic_data: dynamicData,
            status: 'active',
            created_by: session.user.id
          };
        })
        .filter(asset => asset !== null);

      console.log('📊 Ativos válidos para importar:', assetsToImport.length);

      if (assetsToImport.length === 0) {
        updateStep(3, 'error', 'Nenhum ativo válido para importar');
        setIsProcessingImport(false);
        return;
      }

      // Importar ativos
      console.log('💾 Inserindo ativos no banco...');
      
      const { data: insertedAssets, error: insertError } = await supabase
        .from('assets')
        .insert(assetsToImport)
        .select();

      if (insertError) {
        console.error('❌ Erro ao inserir ativos:', insertError);
        updateStep(3, 'error', `Erro ao importar ativos: ${insertError.message}`);
        setIsProcessingImport(false);
        return;
      }

      console.log('✅ Ativos importados:', insertedAssets?.length || 0);
      updateStep(3, 'success', `${insertedAssets?.length || 0} ativos importados`);

      // PASSO 4: Finalizar e atualizar visualização
      updateStep(4, 'processing', 'Atualizando interface...');

      console.log('🔄 Recarregando campos e ativos...');
      await Promise.all([loadAssets(), loadFields()]);

      if (fieldsCreatedCount > 0) {
        updateStep(4, 'success', `Importação concluída! ${fieldsCreatedCount} novos campos adicionados à visualização`);
      } else {
        updateStep(4, 'success', 'Importação concluída com sucesso!');
      }

      console.log('🎉 Importação finalizada com sucesso!');
      console.log(`📈 Resumo: ${insertedAssets?.length || 0} ativos importados, ${fieldsCreatedCount} novos campos criados`);

      // Fechar modal após 2 segundos
      setTimeout(() => {
        setShowImportModal(false);
        setSelectedFile(null);
        setExcelData([]);
        setExcelColumns([]);
        resetSteps();
        setIsProcessingImport(false);
      }, 2000);

    } catch (error) {
      console.error('💥 Erro crítico na importação:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      const currentStep = uploadSteps.find(s => s.status === 'processing');
      if (currentStep) {
        updateStep(currentStep.id, 'error', errorMessage);
      } else {
        updateStep(2, 'error', errorMessage);
      }
      
      setIsProcessingImport(false);
    }
  };

  const handleCancelColumnSelection = () => {
    setShowColumnSelection(false);
    setExcelData([]);
    setExcelColumns([]);
    setSelectedFile(null);
  };

  const handleDeleteAsset = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este ativo?')) return;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/assets-operations`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            action: 'delete',
            assetId: id
          })
        }
      );

      if (response.ok) {
        alert('Ativo excluído com sucesso!');
        loadAssets();
      }
    } catch (error) {
      console.error('Erro ao excluir ativo:', error);
    }
  };

  const addFilterCondition = () => {
    const newCondition: FilterCondition = {
      id: Date.now().toString(),
      field: fields[0]?.field_name || '',
      operator: 'contains',
      value: ''
    };
    setFilterConditions([...filterConditions, newCondition]);
  };

  const removeFilterCondition = (id: string) => {
    setFilterConditions(filterConditions.filter(c => c.id !== id));
  };

  const updateFilterCondition = (id: string, updates: Partial<FilterCondition>) => {
    setFilterConditions(filterConditions.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
  };

  const clearAllFilters = () => {
    setFilterConditions([]);
    setSearchTerm('');
    setSortField('');
    setSortDirection('asc');
  };

  const applyFilters = (assetsList: Asset[]) => {
    let filtered = [...assetsList];

    // Aplicar busca geral
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(asset => {
        return (
          asset.asset_code.toLowerCase().includes(searchLower) ||
          JSON.stringify(asset.dynamic_data).toLowerCase().includes(searchLower)
        );
      });
    }

    // Aplicar filtros avançados
    filterConditions.forEach(condition => {
      if (!condition.value) return;

      filtered = filtered.filter(asset => {
        const fieldValue = String(asset.dynamic_data[condition.field] || '').toLowerCase();
        const conditionValue = condition.value.toLowerCase();

        switch (condition.operator) {
          case 'contains':
            return fieldValue.includes(conditionValue);
          case 'equals':
            return fieldValue === conditionValue;
          case 'starts_with':
            return fieldValue.startsWith(conditionValue);
          case 'ends_with':
            return fieldValue.endsWith(conditionValue);
          case 'not_contains':
            return !fieldValue.includes(conditionValue);
          case 'not_equals':
            return fieldValue !== conditionValue;
          default:
            return true;
        }
      });
    });

    // Aplicar ordenação
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = sortField === 'asset_code' 
          ? a.asset_code 
          : String(a.dynamic_data[sortField] || '');
        let bValue = sortField === 'asset_code' 
          ? b.asset_code 
          : String(b.dynamic_data[sortField] || '');

        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();

        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    return filtered;
  };

  const exportToExcel = () => {
    try {
      console.log('📊 Iniciando exportação para Excel...');
      console.log('Total de ativos:', assets.length);
      
      const filtered = applyFilters(assets);
      console.log('Ativos filtrados:', filtered.length);
      
      if (filtered.length === 0) {
        alert('Nenhum ativo para exportar');
        return;
      }

      // Preparar dados para exportação
      const exportData = filtered.map(asset => {
        const row: any = {
          'Código': asset.asset_code,
          'Status': getStatusLabel(asset.status),
        };

        // Adicionar todos os campos dinâmicos
        Object.entries(asset.dynamic_data).forEach(([key, value]) => {
          row[key] = value || '';
        });

        // Adicionar data de criação
        row['Data de Criação'] = new Date(asset.created_at).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

        return row;
      });

      console.log('Dados preparados para exportação:', exportData.length, 'linhas');
      console.log('Exemplo de linha:', exportData[0]);

      // Criar planilha
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Ajustar largura das colunas
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      ws['!cols'] = colWidths;

      // Criar workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ativos');

      // Gerar nome do arquivo com data
      const fileName = `ativos_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      console.log('Gerando arquivo:', fileName);

      // Fazer download
      XLSX.writeFile(wb, fileName);

      console.log('✅ Exportação concluída com sucesso!');
      alert('Arquivo Excel exportado com sucesso!');

    } catch (error) {
      console.error('❌ Erro ao exportar Excel:', error);
      alert('Erro ao exportar arquivo. Tente novamente.');
    }
  };

  const getOperatorLabel = (operator: string) => {
    const labels: Record<string, string> = {
      'contains': 'Contém',
      'equals': 'Igual a',
      'starts_with': 'Começa com',
      'ends_with': 'Termina com',
      'not_contains': 'Não contém',
      'not_equals': 'Diferente de'
    };
    return labels[operator] || operator;
  };

  const filteredAssets = applyFilters(assets);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'maintenance': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'inactive': return 'Inativo';
      case 'maintenance': return 'Manutenção';
      default: return status;
    }
  };

  const getStepIcon = (status: UploadStep['status']) => {
    switch (status) {
      case 'processing':
        return <Loader className="w-5 h-5 text-teal-600 animate-spin flex-shrink-0" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />;
    }
  };

  const handleAddNewAsset = async () => {
    if (isSavingAsset) return;

    try {
      setIsSavingAsset(true);

      // Verificar se há pelo menos um campo preenchido
      const hasData = Object.values(newAssetData).some(v => v && v.trim() !== '');
      
      if (!hasData) {
        alert('Preencha pelo menos um campo antes de salvar');
        setIsSavingAsset(false);
        return;
      }

      // Obter sessão do usuário
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        alert('Sessão expirada. Faça login novamente.');
        navigate('/login');
        return;
      }

      // Preparar dados do ativo
      const assetToCreate = {
        asset_code: `ASSET-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        dynamic_data: newAssetData,
        status: 'active',
        created_by: session.user.id
      };

      console.log('💾 Criando novo ativo:', assetToCreate);

      // Inserir ativo no banco
      const { data: insertedAsset, error: insertError } = await supabase
        .from('assets')
        .insert([assetToCreate])
        .select()
        .single();

      if (insertError) {
        console.error('❌ Erro ao criar ativo:', insertError);
        alert(`Erro ao criar ativo: ${insertError.message}`);
        setIsSavingAsset(false);
        return;
      }

      console.log('✅ Ativo criado com sucesso:', insertedAsset);
      
      // Recarregar lista de ativos
      await loadAssets();

      // Fechar modal e limpar formulário
      setShowAddModal(false);
      setNewAssetData({});
      setIsSavingAsset(false);

      alert('Ativo criado com sucesso!');

    } catch (error) {
      console.error('💥 Erro ao criar ativo:', error);
      alert('Erro ao criar ativo. Tente novamente.');
      setIsSavingAsset(false);
    }
  };

  const updateNewAssetField = (fieldName: string, value: string) => {
    setNewAssetData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleEditAsset = () => {
    if (!selectedAsset) return;
    setIsEditMode(true);
    setEditedAssetData({ ...selectedAsset.dynamic_data });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedAssetData({});
  };

  const updateEditedField = (fieldName: string, value: string) => {
    setEditedAssetData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedAsset || isSavingEdit) return;

    try {
      setIsSavingEdit(true);

      console.log('💾 Salvando alterações do ativo:', selectedAsset.id);

      // Atualizar ativo no banco
      const { error: updateError } = await supabase
        .from('assets')
        .update({
          dynamic_data: editedAssetData,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedAsset.id);

      if (updateError) {
        console.error('❌ Erro ao atualizar ativo:', updateError);
        alert(`Erro ao salvar alterações: ${updateError.message}`);
        setIsSavingEdit(false);
        return;
      }

      console.log('✅ Ativo atualizado com sucesso');

      // Recarregar lista de ativos
      await loadAssets();

      // Atualizar o ativo selecionado com os novos dados
      setSelectedAsset({
        ...selectedAsset,
        dynamic_data: editedAssetData
      });

      // Sair do modo de edição
      setIsEditMode(false);
      setIsSavingEdit(false);

      alert('Alterações salvas com sucesso!');

    } catch (error) {
      console.error('💥 Erro ao salvar alterações:', error);
      alert('Erro ao salvar alterações. Tente novamente.');
      setIsSavingEdit(false);
    }
  };

  const updatePdfStep = (stepId: number, status: UploadStep['status'], message: string) => {
    setPdfUploadSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, message } : step
    ));
  };

  const resetPdfSteps = () => {
    setPdfUploadSteps([
      { id: 1, title: 'Ler arquivo PDF', status: 'pending', message: 'Aguardando...' },
      { id: 2, title: 'Extrair e analisar texto', status: 'pending', message: 'Aguardando...' },
      { id: 3, title: 'Mapear campos inteligentemente', status: 'pending', message: 'Aguardando...' },
      { id: 4, title: 'Criar campos e importar dados', status: 'pending', message: 'Aguardando...' }
    ]);
  };

  const handlePdfFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedPdfFile(file);
      resetPdfSteps();
    } else {
      alert('Por favor, selecione um arquivo PDF válido');
    }
  };

  const extractTextFromPdf = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n';
    }
    
    return fullText;
  };

  const intelligentFieldMapping = (text: string): Record<string, string> => {
    const data: Record<string, string> = {};
    
    // Padrões comuns de campos
    const patterns = [
      // Código/ID
      { regex: /(?:código|code|id|identificação|número)[\s:]*([A-Z0-9-]+)/gi, field: 'Código' },
      
      // Nome/Descrição
      { regex: /(?:nome|name|descrição|description|título|title)[\s:]*([^\n]+)/gi, field: 'Nome' },
      
      // Modelo
      { regex: /(?:modelo|model|tipo|type)[\s:]*([^\n]+)/gi, field: 'Modelo' },
      
      // Marca/Fabricante
      { regex: /(?:marca|brand|fabricante|manufacturer)[\s:]*([^\n]+)/gi, field: 'Marca' },
      
      // Número de Série
      { regex: /(?:série|serial|s\/n|sn)[\s:]*([A-Z0-9-]+)/gi, field: 'Número de Série' },
      
      // Localização
      { regex: /(?:localização|location|local|endereço|address)[\s:]*([^\n]+)/gi, field: 'Localização' },
      
      // Data de Aquisição
      { regex: /(?:aquisição|acquisition|compra|purchase|data)[\s:]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/gi, field: 'Data de Aquisição' },
      
      // Valor/Preço
      { regex: /(?:valor|value|preço|price|custo|cost)[\s:]*(?:R\$|USD|\$)?\s*([\d.,]+)/gi, field: 'Valor' },
      
      // Status
      { regex: /(?:status|estado|state|condição|condition)[\s:]*([^\n]+)/gi, field: 'Status' },
      
      // Categoria
      { regex: /(?:categoria|category|classe|class)[\s:]*([^\n]+)/gi, field: 'Categoria' },
      
      // Responsável
      { regex: /(?:responsável|responsible|proprietário|owner)[\s:]*([^\n]+)/gi, field: 'Responsável' },
      
      // Departamento
      { regex: /(?:departamento|department|setor|sector)[\s:]*([^\n]+)/gi, field: 'Departamento' },
      
      // Observações
      { regex: /(?:observações|observations|notas|notes|comentários|comments)[\s:]*([^\n]+)/gi, field: 'Observações' }
    ];

    // Aplicar cada padrão
    patterns.forEach(({ regex, field }) => {
      const matches = text.matchAll(regex);
      for (const match of matches) {
        if (match[1] && match[1].trim()) {
          const value = match[1].trim();
          // Limpar o valor
          const cleanValue = value
            .replace(/[\r\n]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          
          if (cleanValue.length > 0 && cleanValue.length < 200) {
            data[field] = cleanValue;
            break; // Pegar apenas a primeira ocorrência
          }
        }
      }
    });

    // Extrair pares chave-valor genéricos (formato: "Campo: Valor")
    const genericPattern = /([A-Za-zÀ-ÿ\s]+):\s*([^\n:]+)/g;
    const genericMatches = text.matchAll(genericPattern);
    
    for (const match of genericMatches) {
      const key = match[1].trim();
      const value = match[2].trim();
      
      // Ignorar se já existe ou se é muito curto/longo
      if (!data[key] && key.length > 2 && key.length < 50 && value.length > 0 && value.length < 200) {
        // Verificar se não é um padrão já capturado
        const isAlreadyCaptured = Object.values(data).some(v => v === value);
        if (!isAlreadyCaptured) {
          data[key] = value;
        }
      }
    }

    return data;
  };

  const handlePdfUpload = async () => {
    if (!selectedPdfFile || isPdfUploading) return;

    setIsPdfUploading(true);

    try {
      // PASSO 1: Ler arquivo PDF
      updatePdfStep(1, 'processing', 'Lendo arquivo PDF...');
      
      console.log('📄 Iniciando leitura do PDF:', selectedPdfFile.name);
      
      const pdfText = await extractTextFromPdf(selectedPdfFile);
      
      console.log('📝 Texto extraído do PDF (primeiros 500 caracteres):');
      console.log(pdfText.substring(0, 500));
      
      if (!pdfText || pdfText.trim().length === 0) {
        updatePdfStep(1, 'error', 'PDF vazio ou não contém texto extraível');
        setIsPdfUploading(false);
        return;
      }

      updatePdfStep(1, 'success', `${pdfText.length} caracteres extraídos`);

      // PASSO 2: Analisar texto
      updatePdfStep(2, 'processing', 'Analisando conteúdo do PDF...');
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updatePdfStep(2, 'success', 'Análise concluída');

      // PASSO 3: Mapear campos inteligentemente
      updatePdfStep(3, 'processing', 'Mapeando campos automaticamente...');
      
      const extractedData = intelligentFieldMapping(pdfText);
      
      console.log('🎯 Dados extraídos:', extractedData);
      console.log('📊 Total de campos identificados:', Object.keys(extractedData).length);
      
      if (Object.keys(extractedData).length === 0) {
        updatePdfStep(3, 'error', 'Nenhum campo identificado no PDF');
        setIsPdfUploading(false);
        return;
      }

      updatePdfStep(3, 'success', `${Object.keys(extractedData).length} campos identificados`);

      // PASSO 4: Criar campos e importar dados
      updatePdfStep(4, 'processing', 'Criando campos e importando dados...');

      // Verificar sessão
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        updatePdfStep(4, 'error', 'Sessão expirada. Faça login novamente.');
        setIsPdfUploading(false);
        navigate('/login');
        return;
      }

      // Buscar campos existentes
      const { data: existingFields, error: fetchError } = await supabase
        .from('dynamic_fields')
        .select('field_name');

      if (fetchError) {
        updatePdfStep(4, 'error', `Erro ao buscar campos: ${fetchError.message}`);
        setIsPdfUploading(false);
        return;
      }

      const existingFieldNames = new Set(existingFields?.map(f => f.field_name) || []);

      // Criar apenas campos novos
      const fieldNames = Object.keys(extractedData);
      const newFields = fieldNames
        .filter(fieldName => !existingFieldNames.has(fieldName))
        .map((fieldName, index) => ({
          field_name: fieldName,
          field_type: 'text',
          field_order: (existingFields?.length || 0) + index,
          is_required: false,
          created_by: session.user.id
        }));

      console.log('🆕 Novos campos a criar:', newFields.length);

      let fieldsCreatedCount = 0;

      if (newFields.length > 0) {
        for (const field of newFields) {
          const { error: insertError } = await supabase
            .from('dynamic_fields')
            .insert([field]);

          if (insertError) {
            if (!insertError.message.includes('duplicate') && !insertError.message.includes('unique')) {
              console.error(`❌ Erro ao criar campo "${field.field_name}":`, insertError);
            }
          } else {
            fieldsCreatedCount++;
            console.log(`✅ Campo "${field.field_name}" criado`);
          }
        }
      }

      // Criar o ativo com os dados extraídos
      const assetToCreate = {
        asset_code: `ASSET-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        dynamic_data: extractedData,
        status: 'active',
        created_by: session.user.id
      };

      console.log('💾 Criando ativo:', assetToCreate);

      const { data: insertedAsset, error: insertError } = await supabase
        .from('assets')
        .insert([assetToCreate])
        .select()
        .single();

      if (insertError) {
        console.error('❌ Erro ao criar ativo:', insertError);
        updatePdfStep(4, 'error', `Erro ao importar: ${insertError.message}`);
        setIsPdfUploading(false);
        return;
      }

      console.log('✅ Ativo criado com sucesso:', insertedAsset);

      updatePdfStep(4, 'success', `Ativo importado com ${Object.keys(extractedData).length} campos`);

      // Recarregar dados
      await Promise.all([loadAssets(), loadFields()]);

      console.log('🎉 Importação de PDF concluída com sucesso!');

      // Fechar modal após 2 segundos
      setTimeout(() => {
        setShowPdfImportModal(false);
        setSelectedPdfFile(null);
        resetPdfSteps();
        setIsPdfUploading(false);
      }, 2000);

    } catch (error) {
      console.error('💥 Erro crítico na importação de PDF:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      const currentStep = pdfUploadSteps.find(s => s.status === 'processing');
      if (currentStep) {
        updatePdfStep(currentStep.id, 'error', errorMessage);
      } else {
        updatePdfStep(1, 'error', errorMessage);
      }
      
      setIsPdfUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestão de Ativos</h1>
            <p className="text-gray-600 mt-2">Cadastro e monitoramento de ativos</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <FileSpreadsheet className="w-5 h-5" />
              Exportar Excel
            </button>
            <button
              onClick={() => {
                setShowPdfImportModal(true);
                setSelectedPdfFile(null);
                resetPdfSteps();
                setIsPdfUploading(false);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <FileText className="w-5 h-5" />
              Importar PDF
            </button>
            <button
              onClick={() => {
                setShowImportModal(true);
                setSelectedFile(null);
                resetSteps();
                setIsUploading(false);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <Upload className="w-5 h-5" />
              Importar Excel
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <Plus className="w-5 h-5" />
              Novo Ativo
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="p-4 space-y-4">
            {/* Busca Geral */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar em todos os campos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                />
              </div>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm font-medium ${
                  showAdvancedFilters || filterConditions.length > 0
                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filtros Avançados
                {filterConditions.length > 0 && (
                  <span className="bg-white text-teal-600 px-2 py-0.5 rounded-full text-xs font-bold">
                    {filterConditions.length}
                  </span>
                )}
              </button>
            </div>

            {/* Filtros Avançados */}
            {showAdvancedFilters && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Filtros Personalizados</h3>
                  {filterConditions.length > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-xs text-red-600 hover:text-red-700 font-medium cursor-pointer"
                    >
                      Limpar Todos
                    </button>
                  )}
                </div>

                {/* Ordenação */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Ordenar por
                    </label>
                    <select
                      value={sortField}
                      onChange={(e) => setSortField(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer"
                    >
                      <option value="">Sem ordenação</option>
                      <option value="asset_code">Código</option>
                      {fields.map(field => (
                        <option key={field.id} value={field.field_name}>
                          {field.field_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Direção
                    </label>
                    <select
                      value={sortDirection}
                      onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                      disabled={!sortField}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="asc">Crescente (A-Z)</option>
                      <option value="desc">Decrescente (Z-A)</option>
                    </select>
                  </div>
                </div>

                {/* Condições de Filtro */}
                {filterConditions.map((condition, index) => (
                  <div key={condition.id} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Campo
                        </label>
                        <select
                          value={condition.field}
                          onChange={(e) => updateFilterCondition(condition.id, { field: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer"
                        >
                          {fields.map(field => (
                            <option key={field.id} value={field.field_name}>
                              {field.field_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Operador
                        </label>
                        <select
                          value={condition.operator}
                          onChange={(e) => updateFilterCondition(condition.id, { operator: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer"
                        >
                          <option value="contains">Contém</option>
                          <option value="equals">Igual a</option>
                          <option value="starts_with">Começa com</option>
                          <option value="ends_with">Termina com</option>
                          <option value="not_contains">Não contém</option>
                          <option value="not_equals">Diferente de</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Valor
                        </label>
                        <input
                          type="text"
                          value={condition.value}
                          onChange={(e) => updateFilterCondition(condition.id, { value: e.target.value })}
                          placeholder="Digite o valor..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeFilterCondition(condition.id)}
                      className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="Remover filtro"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  onClick={addFilterCondition}
                  disabled={fields.length === 0}
                  className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-teal-500 hover:text-teal-600 transition-colors cursor-pointer text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + Adicionar Condição de Filtro
                </button>
              </div>
            )}

            {/* Resumo dos Filtros Ativos */}
            {(filterConditions.length > 0 || sortField) && (
              <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200">
                <span className="text-xs font-medium text-gray-600">Filtros ativos:</span>
                {sortField && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                    Ordenado: {sortField} ({sortDirection === 'asc' ? '↑' : '↓'})
                  </span>
                )}
                {filterConditions.map(condition => (
                  condition.value && (
                    <span key={condition.id} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs">
                      {condition.field} {getOperatorLabel(condition.operator)} "{condition.value}"
                    </span>
                  )
                ))}
              </div>
            )}

            {/* Contador de Resultados */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Mostrando <span className="font-semibold text-gray-900">{filteredAssets.length}</span> de{' '}
                <span className="font-semibold text-gray-900">{assets.length}</span> ativos
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Código</th>
                  {fields.map(field => (
                    <th key={field.id} className="px-6 py-4 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {field.field_name}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900 whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAssets.length === 0 ? (
                  <tr>
                    <td colSpan={fields.length + 3} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Search className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-600 font-medium mb-1">Nenhum ativo encontrado</p>
                        <p className="text-sm text-gray-500">
                          {assets.length === 0 
                            ? 'Importe um arquivo Excel ou adicione um novo ativo para começar'
                            : 'Tente ajustar os filtros de busca'
                          }
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAssets.map(asset => (
                    <tr key={asset.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium whitespace-nowrap">{asset.asset_code}</td>
                      {fields.map(field => (
                        <td key={field.id} className="px-6 py-4 text-sm text-gray-600">
                          <div className="max-w-xs truncate" title={asset.dynamic_data[field.field_name] || '-'}>
                            {asset.dynamic_data[field.field_name] || '-'}
                          </div>
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                          {getStatusLabel(asset.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedAsset(asset)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAsset(asset.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Importar Ativos do Excel</h3>
                  <p className="text-sm text-gray-600">Acompanhe o progresso da importação</p>
                </div>
              </div>

              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Formato do arquivo:</strong> A primeira linha deve conter os nomes dos campos (cabeçalho) e as linhas seguintes devem conter os dados dos ativos.
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block w-full">
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
                    selectedFile 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
                  }`}>
                    <Upload className={`w-12 h-12 mx-auto mb-3 ${selectedFile ? 'text-teal-600' : 'text-gray-400'}`} />
                    {selectedFile ? (
                      <>
                        <p className="text-sm font-medium text-teal-900 mb-1">{selectedFile.name}</p>
                        <p className="text-xs text-teal-700">Arquivo selecionado - Clique para alterar</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-1">Clique para selecionar um arquivo</p>
                        <p className="text-xs text-gray-500">Formatos aceitos: .xlsx, .xls</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
              </div>

              {selectedFile && (
                <div className="mb-6 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Progresso da Importação:</h4>
                  {uploadSteps.map((step, index) => (
                    <div key={step.id} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        {getStepIcon(step.status)}
                        {index < uploadSteps.length - 1 && (
                          <div className={`w-0.5 h-8 mt-2 ${
                            step.status === 'success' ? 'bg-green-600' : 'bg-gray-200'
                          }`} />
                        )}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className={`text-sm font-medium ${
                          step.status === 'error' ? 'text-red-900' :
                          step.status === 'success' ? 'text-green-900' :
                          step.status === 'processing' ? 'text-teal-900' :
                          'text-gray-600'
                        }`}>
                          {step.title}
                        </p>
                        <p className={`text-xs mt-0.5 ${
                          step.status === 'error' ? 'text-red-600' :
                          step.status === 'success' ? 'text-green-600' :
                          step.status === 'processing' ? 'text-teal-600' :
                          'text-gray-500'
                        }`}>
                          {step.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                    resetSteps();
                    setIsUploading(false);
                  }}
                  disabled={isUploading}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isUploading ? 'Aguarde...' : 'Cancelar'}
                </button>
                <button
                  onClick={handleFileUpload}
                  disabled={!selectedFile || isUploading}
                  className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isUploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Processando...
                    </span>
                  ) : (
                    'Iniciar Importação'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Detalhes/Edição do Ativo */}
        {selectedAsset && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Eye className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {isEditMode ? 'Editar Ativo' : 'Detalhes do Ativo'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Código: <span className="font-medium text-gray-900">{selectedAsset.asset_code}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedAsset(null);
                      setIsEditMode(false);
                      setEditedAssetData({});
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                    title="Fechar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Status Badge */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <span className={`inline-flex px-4 py-2 rounded-lg text-sm font-medium ${getStatusColor(selectedAsset.status)}`}>
                      {getStatusLabel(selectedAsset.status)}
                    </span>
                  </div>

                  {/* Campos Dinâmicos */}
                  {Object.entries(isEditMode ? editedAssetData : selectedAsset.dynamic_data).map(([key, value]) => (
                    <div key={key} className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700">
                        {key}
                      </label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={editedAssetData[key] || ''}
                          onChange={(e) => updateEditedField(key, e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                          placeholder={`Digite ${key.toLowerCase()}...`}
                        />
                      ) : (
                        <div className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-900">{value || '-'}</p>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Data de Criação */}
                  <div className="md:col-span-2 space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Data de Criação
                    </label>
                    <div className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-900">
                        {new Date(selectedAsset.created_at).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  {isEditMode ? (
                    <>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSavingEdit}
                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSavingEdit}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {isSavingEdit ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Salvar Alterações
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setSelectedAsset(null);
                          setIsEditMode(false);
                          setEditedAssetData({});
                        }}
                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer font-medium"
                      >
                        Fechar
                      </button>
                      <button
                        onClick={handleEditAsset}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-medium"
                      >
                        <Edit className="w-4 h-4" />
                        Editar Ativo
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Adicionar Novo Ativo */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Plus className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Novo Ativo</h3>
                  <p className="text-sm text-gray-600">Preencha os campos para cadastrar um novo ativo</p>
                </div>
              </div>

              {fields.length === 0 ? (
                <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <p className="text-amber-800 font-medium mb-2">Nenhum campo cadastrado</p>
                  <p className="text-sm text-amber-700">
                    Importe uma planilha Excel primeiro para criar os campos dinâmicos
                  </p>
                </div>
              ) : (
                <div className="space-y-4 mb-6">
                  {fields.map(field => (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {field.field_name}
                        {field.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <input
                        type="text"
                        value={newAssetData[field.field_name] || ''}
                        onChange={(e) => updateNewAssetField(field.field_name, e.target.value)}
                        placeholder={`Digite ${field.field_name.toLowerCase()}...`}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewAssetData({});
                  }}
                  disabled={isSavingAsset}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddNewAsset}
                  disabled={isSavingAsset || fields.length === 0}
                  className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isSavingAsset ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Salvando...
                    </span>
                  ) : (
                    'Salvar Ativo'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Seleção de Colunas */}
        {showColumnSelection && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <FileSpreadsheet className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Selecionar Colunas para Importar</h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Escolha quais colunas você deseja importar do arquivo Excel
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCancelColumnSelection}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                    title="Cancelar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="px-6 pt-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Dica:</strong> Selecione apenas as colunas que você precisa. As colunas não selecionadas serão ignoradas durante a importação.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-3 flex items-center justify-between border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAllColumns}
                    className="px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer font-medium"
                  >
                    Selecionar Todas
                  </button>
                  <button
                    onClick={deselectAllColumns}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer font-medium"
                  >
                    Desmarcar Todas
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-teal-600">
                    {excelColumns.filter(c => c.selected).length}
                  </span>
                  {' '}de{' '}
                  <span className="font-semibold text-gray-900">
                    {excelColumns.length}
                  </span>
                  {' '}colunas selecionadas
                </p>
              </div>

              {/* Columns List */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {excelColumns.map((column, index) => (
                    <div
                      key={index}
                      className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                        column.selected
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={() => toggleColumnSelection(column.name)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              column.selected
                                ? 'bg-teal-600 border-teal-600'
                                : 'bg-white border-gray-300'
                            }`}
                          >
                            {column.selected && (
                              <CheckCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`text-sm font-semibold mb-2 ${
                            column.selected ? 'text-teal-900' : 'text-gray-900'
                          }`}>
                            {column.name}
                          </h4>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 mb-1">
                              Exemplos de dados:
                            </p>
                            {column.sampleData.map((sample, sampleIndex) => (
                              <p
                                key={sampleIndex}
                                className={`text-xs truncate ${
                                  column.selected ? 'text-teal-700' : 'text-gray-600'
                                }`}
                              >
                                • {sample}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelColumnSelection}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmImport}
                    disabled={excelColumns.filter(c => c.selected).length === 0}
                    className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Importar Colunas Selecionadas
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Importação de PDF */}
        {showPdfImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    Importar Ativo do PDF
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </h3>
                  <p className="text-sm text-gray-600">Extração inteligente de dados</p>
                </div>
              </div>

              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800 mb-2">
                  <strong>🤖 Importação Inteligente:</strong>
                </p>
                <ul className="text-sm text-purple-700 space-y-1 ml-4">
                  <li>• Extrai automaticamente texto do PDF</li>
                  <li>• Identifica campos como: Código, Nome, Modelo, Marca, Série, etc.</li>
                  <li>• Mapeia dados inteligentemente usando IA</li>
                  <li>• Cria campos e importa o ativo automaticamente</li>
                </ul>
              </div>
              
              <div className="mb-6">
                <label className="block w-full">
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
                    selectedPdfFile 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
                  }`}>
                    <FileText className={`w-12 h-12 mx-auto mb-3 ${selectedPdfFile ? 'text-purple-600' : 'text-gray-400'}`} />
                    {selectedPdfFile ? (
                      <>
                        <p className="text-sm font-medium text-purple-900 mb-1">{selectedPdfFile.name}</p>
                        <p className="text-xs text-purple-700">Arquivo selecionado - Clique para alterar</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-1">Clique para selecionar um arquivo PDF</p>
                        <p className="text-xs text-gray-500">Formatos aceitos: .pdf</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfFileSelect}
                    disabled={isPdfUploading}
                    className="hidden"
                  />
                </label>
              </div>

              {selectedPdfFile && (
                <div className="mb-6 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Progresso da Importação:</h4>
                  {pdfUploadSteps.map((step, index) => (
                    <div key={step.id} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        {getStepIcon(step.status)}
                        {index < pdfUploadSteps.length - 1 && (
                          <div className={`w-0.5 h-8 mt-2 ${
                            step.status === 'success' ? 'bg-green-600' : 'bg-gray-200'
                          }`} />
                        )}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className={`text-sm font-medium ${
                          step.status === 'error' ? 'text-red-900' :
                          step.status === 'success' ? 'text-green-900' :
                          step.status === 'processing' ? 'text-purple-900' :
                          'text-gray-600'
                        }`}>
                          {step.title}
                        </p>
                        <p className={`text-xs mt-0.5 ${
                          step.status === 'error' ? 'text-red-600' :
                          step.status === 'success' ? 'text-green-600' :
                          step.status === 'processing' ? 'text-purple-600' :
                          'text-gray-500'
                        }`}>
                          {step.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPdfImportModal(false);
                    setSelectedPdfFile(null);
                    resetPdfSteps();
                    setIsPdfUploading(false);
                  }}
                  disabled={isPdfUploading}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isPdfUploading ? 'Aguarde...' : 'Cancelar'}
                </button>
                <button
                  onClick={handlePdfUpload}
                  disabled={!selectedPdfFile || isPdfUploading}
                  className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isPdfUploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Processando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Importar Inteligentemente
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
